import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

type DirectoryStore = {
  id: number;
  slug: string;
  name: string;
  address?: string;
  cap?: string;
  comune?: string;
  provincia?: string;
  tagline?: string;
  cuisine_summary?: string;
  price_range?: string;
  cover_url?: string;
  opening_hours?: string;
  open_state?: string;
  open_label?: string;
  is_open?: boolean | null;
};

function formatLocation(store: DirectoryStore) {
  const address = String(store.address || '').trim();
  const cap = String(store.cap || '').trim();
  const comune = String(store.comune || '').trim();
  const provincia = String(store.provincia || '').trim().toUpperCase();
  const cityParts = [cap, comune, provincia ? `(${provincia})` : ''].filter(Boolean);
  const cityLine = cityParts.join(' ');
  if (address && cityLine) return `${address}, ${cityLine}`;
  return address || cityLine || '';
}

function formatMetaLine(store: DirectoryStore) {
  return [store.cuisine_summary, store.price_range]
    .map((v) => String(v || '').trim())
    .filter(Boolean)
    .join(' · ');
}

export function TenantDirectoryPage() {
  const [search, setSearch] = useState('');
  const [openOnly, setOpenOnly] = useState(false);
  const [storesRaw, setStoresRaw] = useState<DirectoryStore[]>([]);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const handle = window.setTimeout(() => {
      setFetching(true);
      void fetch(`/api/v1/public/directory/stores?search=${encodeURIComponent(search.trim())}`)
        .then(async (res) => {
          const body = await res.json().catch(() => ([]));
          if (!res.ok) {
            const msg =
              body && typeof body === 'object' && 'message' in body
                ? String((body as { message?: string }).message)
                : `Errore ${res.status}`;
            throw new Error(msg);
          }
          if (!cancelled) setStoresRaw(Array.isArray(body) ? body : []);
        })
        .catch((err) => {
          if (!cancelled) {
            setStoresRaw([]);
            setError(err instanceof Error ? err.message : String(err));
          }
        })
        .finally(() => {
          if (!cancelled) {
            setBusy(false);
            setFetching(false);
          }
        });
    }, search ? 220 : 0);

    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [search]);

  const stores = useMemo(() => {
    if (!openOnly) return storesRaw;
    return storesRaw.filter((s) => s.open_state === 'open' || s.is_open === true);
  }, [storesRaw, openOnly]);

  return (
    <div className="sk-locali">
      <div className="sk-locali__glow" aria-hidden="true" />

      <header className="sk-locali__header">
        <Link to="/" className="sk-locali__brand">
          StrikerPos
        </Link>
        <h1 className="sk-locali__title">Trova un locale e prenota</h1>
        <p className="sk-locali__lead">
          Scopri i locali partner e apri la vetrina per menu e prenotazioni.
        </p>
      </header>

      <div className="sk-locali__filters">
        <label className="sk-locali__search">
          <span className="sk-sr-only">Cerca locale</span>
          <input
            type="search"
            placeholder="Cerca per nome o cucina…"
            value={search}
            onChange={(e) => {
              setError(null);
              setSearch(e.target.value);
            }}
          />
        </label>
        <div className="sk-locali__filter-row">
          <button
            type="button"
            className={`sk-locali__chip${openOnly ? ' sk-locali__chip--on' : ''}`}
            aria-pressed={openOnly}
            onClick={() => setOpenOnly((v) => !v)}
          >
            Solo aperti
          </button>
          {!busy && !error ? (
            <p className="sk-locali__count">
              {stores.length} local{stores.length === 1 ? 'e' : 'i'}
              {search.trim() ? ` per “${search.trim()}”` : ''}
              {openOnly ? ' · aperti ora' : ''}
            </p>
          ) : null}
          {fetching && !busy ? <p className="sk-locali__count sk-locali__count--right">Aggiorno…</p> : null}
        </div>
      </div>

      <main className="sk-locali__main">
        {busy ? <p className="sk-locali__status">Caricamento locali…</p> : null}
        {error ? <p className="sk-locali__status sk-locali__status--err">{error}</p> : null}

        {!busy && !error && stores.length === 0 ? (
          <div className="sk-locali__empty">
            <p className="sk-locali__empty-title">Nessun locale qui</p>
            <p className="sk-locali__empty-text">
              {openOnly
                ? 'Nessun locale risulta aperto ora. Disattiva “Solo aperti” per vedere tutti.'
                : search.trim()
                  ? `Nessun risultato per “${search.trim()}”.`
                  : 'Non ci sono locali in vetrina al momento.'}
            </p>
          </div>
        ) : null}

        <ul className="sk-locali__list">
          {stores.map((store) => {
            const slug = String(store.slug || '').trim();
            if (!slug) return null;
            const openState = String(store.open_state || 'unknown');
            const coverUrl = String(store.cover_url || '').trim();
            const tagline = String(store.tagline || '').trim();
            const metaLine = formatMetaLine(store);
            const location = formatLocation(store);
            const name = String(store.name || '').trim() || 'Locale';

            return (
              <li key={store.id || slug}>
                <Link className="sk-locali__card" to={`/r/${encodeURIComponent(slug)}`}>
                  <div className={`sk-locali__cover${coverUrl ? '' : ' sk-locali__cover--empty'}`}>
                    {coverUrl ? <img src={coverUrl} alt="" loading="lazy" /> : null}
                    <span className={`sk-locali__badge sk-locali__badge--${openState}`}>
                      {store.open_label || 'Orari non disponibili'}
                    </span>
                  </div>
                  <div className="sk-locali__body">
                    <h2 className="sk-locali__name">{name}</h2>
                    {tagline ? <p className="sk-locali__tagline">{tagline}</p> : null}
                    {metaLine ? <p className="sk-locali__meta">{metaLine}</p> : null}
                    {location ? <p className="sk-locali__location">{location}</p> : null}
                    <p className="sk-locali__hours">{store.opening_hours || 'Orari non disponibili'}</p>
                    <p className="sk-locali__cta">Vedi e prenota →</p>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>

        <p className="sk-locali__footer-nav">
          <Link to="/">← Home</Link>
          <Link to="/register">Apri il tuo locale</Link>
        </p>
      </main>
    </div>
  );
}
